import { Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import {
  Reveal,
  CountUp,
  MotionPath,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { Button } from "@project/components/ui/button";
import { useI18n } from "@project/lib/use-i18n";
import {
  ShieldCheck,
  FileText,
  Award,
  TrendingUp,
  Heart,
  Stethoscope,
  Sprout,
  ShoppingBag,
  ArrowRight,
  BadgeCheck,
} from "lucide-react";

const utilization = [
  {
    labelKey: "trust.transparency.utilization.residentCare",
    pct: 42,
    icon: Heart,
    accent: "from-primary/90 to-primary",
  },
  {
    labelKey: "trust.transparency.utilization.healthcare",
    pct: 24,
    icon: Stethoscope,
    accent: "from-emerald-600 to-emerald-500",
  },
  {
    labelKey: "trust.transparency.utilization.gaushala",
    pct: 14,
    icon: Sprout,
    accent: "from-amber-600 to-amber-500",
  },
  {
    labelKey: "trust.transparency.utilization.gruhUdhyog",
    pct: 10,
    icon: ShoppingBag,
    accent: "from-rose-600 to-rose-500",
  },
  {
    labelKey: "trust.transparency.utilization.operations",
    pct: 10,
    icon: ShieldCheck,
    accent: "from-primary/90 to-primary",
  },
];

const highlights = [
  {
    year: "2026",
    titleKey: "trust.transparency.highlights.y2026.title",
    bodyKey: "trust.transparency.highlights.y2026.body",
  },
  {
    year: "2025",
    titleKey: "trust.transparency.highlights.y2025.title",
    bodyKey: "trust.transparency.highlights.y2025.body",
  },
  {
    year: "2024",
    titleKey: "trust.transparency.highlights.y2024.title",
    bodyKey: "trust.transparency.highlights.y2024.body",
  },
  {
    year: "2023",
    titleKey: "trust.transparency.highlights.y2023.title",
    bodyKey: "trust.transparency.highlights.y2023.body",
  },
];

const reports = [
  {
    titleKey: "trust.transparency.reports.annual.title",
    size: "2.4 MB · PDF",
    noteKey: "trust.transparency.reports.annual.note",
  },
  {
    titleKey: "trust.transparency.reports.impact.title",
    size: "1.1 MB · PDF",
    noteKey: "trust.transparency.reports.impact.note",
  },
  {
    titleKey: "trust.transparency.reports.form10b.title",
    size: "320 KB · PDF",
    noteKey: "trust.transparency.reports.form10b.note",
  },
  {
    titleKey: "trust.transparency.reports.certs.title",
    size: "280 KB · PDF",
    noteKey: "trust.transparency.reports.certs.note",
  },
];

const achievements = [
  {
    icon: BadgeCheck,
    titleKey: "trust.transparency.achievements.certified.title",
    bodyKey: "trust.transparency.achievements.certified.body",
  },
  {
    icon: Award,
    titleKey: "trust.transparency.achievements.samman.title",
    bodyKey: "trust.transparency.achievements.samman.body",
  },
  {
    icon: TrendingUp,
    titleKey: "trust.transparency.achievements.audit.title",
    bodyKey: "trust.transparency.achievements.audit.body",
  },
];

export default function TransparencyPage() {
  const { t } = useI18n();
  return (
    <PageShell>
      <PageHero
        eyebrow={t("trust.transparency.hero.eyebrow")}
        title={t("trust.transparency.hero.title")}
        subtitle={t("trust.transparency.hero.subtitle")}
      />

      {/* Headline metrics */}
      <section className="border-border/60 bg-card/40 border-b">
        <Stagger className="mx-auto grid max-w-site gap-4 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              n: 2,
              suffix: "Cr+",
              label: t("trust.transparency.metrics.donations"),
            },
            {
              n: 1000,
              suffix: "+",
              label: t("trust.transparency.metrics.families"),
            },
            {
              n: 120,
              suffix: "+",
              label: t("trust.transparency.metrics.lives"),
            },
            {
              n: 100,
              suffix: "%",
              label: t("trust.transparency.metrics.donorFunded"),
            },
          ].map((s, i) => (
            <StaggerItem key={s.label} index={i} from="zoom">
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-background p-5">
                <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-serif text-3xl font-semibold text-foreground">
                    <CountUp to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Utilization */}
      <section className="mx-auto max-w-site px-4 py-16">
        <Reveal variant="morph" className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("trust.transparency.utilization.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground md:text-4xl">
            {t("trust.transparency.utilization.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            {t("trust.transparency.utilization.subtitle")}
          </p>
        </Reveal>
        <Stagger className="mx-auto mt-10 grid max-w-4xl gap-4" step={110}>
          {utilization.map((u, i) => (
            <StaggerItem key={u.labelKey} index={i} from="left">
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className={`grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br ${u.accent} text-white shadow-sm`}
                    >
                      <u.icon className="h-4 w-4" />
                    </div>
                    <span className="font-semibold text-foreground">
                      {t(u.labelKey)}
                    </span>
                  </div>
                  <span className="font-bold text-primary">{u.pct}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full" style={{ width: `${u.pct}%` }}>
                    <MotionPath
                      className="h-full w-full"
                      lineClassName={`rounded-full bg-gradient-to-r ${u.accent}`}
                      delay={300 + i * 110}
                    />
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Annual highlights */}
      <section className="bg-secondary/40">
        <div className="mx-auto max-w-site px-4 py-16">
          <Reveal variant="fade" className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
              {t("trust.transparency.highlights.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
              <RevealText delay={120}>
                {t("trust.transparency.highlights.title")}
              </RevealText>
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-5 md:grid-cols-2">
            {highlights.map((h, i) => (
              <StaggerItem
                key={h.year}
                index={i}
                from={i % 2 === 0 ? "left" : "right"}
                className="h-full"
              >
                <article className="card-lift h-full rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-baseline gap-3">
                    <span className="font-sans text-lg text-primary">
                      {h.year}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  <h3 className="mt-2 font-serif text-xl text-foreground">
                    {t(h.titleKey)}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t(h.bodyKey)}
                  </p>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Reports */}
      <section className="mx-auto max-w-site px-4 py-16">
        <Reveal className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("trust.transparency.reports.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
            {t("trust.transparency.reports.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            {t("trust.transparency.reports.subtitle")}
          </p>
        </Reveal>
        <Stagger className="mt-10 grid gap-4 sm:grid-cols-2">
          {reports.map((r, i) => (
            <StaggerItem key={r.titleKey} index={i}>
              <article className="card-lift flex items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="!text-[21px] !font-sans !font-semibold text-foreground">
                    {t(r.titleKey)}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t(r.noteKey)}
                  </p>
                  <p className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {r.size}
                  </p>
                </div>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  <Link to="/contact">
                    {t("trust.transparency.reports.request")}
                  </Link>
                </Button>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Achievements */}
      <section className="bg-card/40 border-border/60 border-y">
        <div className="mx-auto max-w-site px-4 py-16">
          <Reveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
              {t("trust.transparency.achievements.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
              {t("trust.transparency.achievements.title")}
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-5 md:grid-cols-3" step={140}>
            {achievements.map((a, i) => (
              <StaggerItem
                key={a.titleKey}
                index={i}
                from="zoom"
                className="h-full"
              >
                <article className="card-lift h-full rounded-3xl border border-border bg-card p-7 shadow-sm">
                  <div className="shadow-primary/20 grid h-12 w-12 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                    <a.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-foreground">
                    {t(a.titleKey)}
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground">
                    {t(a.bodyKey)}
                  </p>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center">
          <Reveal variant="morph">
            <ShieldCheck className="mx-auto h-7 w-7 text-gold" />
            <h2 className="mt-4 font-serif text-3xl font-semibold md:text-4xl">
              {t("trust.transparency.cta.title")}
            </h2>
            <p className="text-primary-foreground/80 mx-auto mt-3 max-w-xl">
              {t("trust.transparency.cta.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="hover:bg-gold/90 btn-lift rounded-full bg-gold text-gold-foreground"
              >
                <Link to="/donate">{t("trust.transparency.cta.donate")}</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-primary-foreground/40 hover:bg-primary-foreground/10 btn-lift rounded-full bg-transparent text-primary-foreground hover:text-primary-foreground"
              >
                <Link to="/contact">
                  {t("trust.transparency.cta.talk")}{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
